/**
 * M-X23 — Sub-agents API client.
 *
 * Companion to apps/chaos-gateway/src/sub-agents.ts. The tab fetches the
 * per-session list at 2 s cadence.
 *
 * Telemetry (migration 0125, 2026-07-17): `model`, `tokensInput`,
 * `tokensOutput` and `toolCallsCount` used to be structurally worthless —
 * `model` was the hardcoded literal 'claude-cli-subscription' on every row
 * and the three counters were never written at all, so every live row read
 * 0. The gateway now tails each sub-agent's own transcript
 * (`subagents/agent-<agentId>.jsonl`) and they carry real values, plus the
 * cache counters that turn out to BE the cost story: on real data one
 * sub-agent reported input=11,398 against cache_read=28,724,202, so
 * `tokensInput` alone understates consumption by ~2500x. Never render input
 * as if it were the total.
 *
 * kill() does NOT stop a running agent — see the tab for the full story.
 */
import { apiJson } from './client';

export type SubAgentStatus = 'running' | 'done' | 'failed' | 'aborted';

export interface SubAgentRow {
  id: string;
  runId: string;
  sessionId: string;
  cardId: string;
  toolUseId: string;
  description: string | null;
  subagentType: string | null;
  /**
   * The REAL model, e.g. 'claude-opus-4-8'. null = not learned yet (the row
   * was just created and the scanner has not read the sub-agent's first
   * assistant record — a sub-1.5 s window). Render null as "resolving…",
   * NEVER as a placeholder id.
   */
  model: string | null;
  status: SubAgentStatus;
  currentActivity: string | null;
  startedAt: string;
  endedAt: string | null;
  tokensInput: number;
  tokensOutput: number;
  toolCallsCount: number;
  exitSummary: string | null;
  /** Server-computed (now() - started_at), already capped at 4 weeks. */
  elapsedMs: number;

  // ── added by migration 0125 ──
  /** on-disk <agentId> of agent-<agentId>.jsonl; the link back to the transcript */
  agentId: string | null;
  /**
   * `toolUseId` of the sub-agent that spawned this one; null = depth-1.
   * Matches another row's `toolUseId`. This is the only way nesting is
   * visible: a sub-agent's own Task blocks go to ITS transcript, so before
   * 0125 anything deeper than depth-1 was structurally invisible (on one
   * measured session, 4 sub-agents ran and 1 row existed).
   */
  parentToolUseId: string | null;
  /** Effort tier the parent session was launched with (chaos.sessions.effort). */
  effort: string | null;
  /** The real cost driver — routinely orders of magnitude above tokensInput. */
  tokensCacheRead: number;
  tokensCacheCreation: number;
  /** Last record this sub-agent wrote to its own transcript. */
  lastActivityAt: string | null;
  /**
   * Age of lastActivityAt — the hung-vs-progressing signal. Raw and ungated
   * on status by design: the server refuses to guess the "how long is too
   * long" threshold, so that call is made in the tab (see STALL_MS). null =
   * never seen writing. Capped at 4 weeks server-side.
   */
  lastActivityAgeMs: number | null;
}

export interface KillSubAgentResult {
  status: 'aborted';
  sub_agent_id: string;
  run_id: string;
  /** True when the parent run row was also flipped out of an active state. */
  parent_run_aborted: boolean;
}

export const subAgentsApi = {
  /**
   * List up to 50 most recent sub-agents for this session, active first.
   * Returns an empty array for sessions that have never spawned one.
   */
  list(sessionId: string): Promise<SubAgentRow[]> {
    return apiJson<{ items: SubAgentRow[] }>(
      `/sessions/${encodeURIComponent(sessionId)}/sub-agents`,
    ).then((r) => r.items);
  },

  /**
   * Full detail for one row — same shape as list() entries but
   * useful when deep-linked or when polling a single row.
   */
  get(id: string): Promise<SubAgentRow> {
    return apiJson<SubAgentRow>(`/sub-agents/${encodeURIComponent(id)}`);
  },

  /**
   * Mark one sub-agent row aborted.
   *
   * NAME IS HISTORICAL. This does not kill anything: the route only stops a
   * container when `session_runs.container_name` is set, and that column is
   * NULL for every run in prod, so the docker branch is skipped and all that
   * lands is two row updates (the run + this sub-agent). The agent process
   * keeps running. See the tab for what to tell the user.
   */
  kill(id: string): Promise<KillSubAgentResult> {
    return apiJson<KillSubAgentResult>(
      `/sub-agents/${encodeURIComponent(id)}/kill`,
      { method: 'POST', json: {} },
    );
  },
};

export const subAgentsKeys = {
  all: ['sub-agents'] as const,
  list: (sessionId: string) => [...subAgentsKeys.all, 'session', sessionId] as const,
  detail: (id: string) => [...subAgentsKeys.all, 'detail', id] as const,
};
